/* collection.js: ported from zum-impulse, Alpine components registered on alpine:init.
   - collectionGrid: filters through the Section Rendering API (sidebar, drawer, top quick links,
     active-tag removal), sort, filter drawer, back/forward. Used by main-collection and main-search.
   - priceRange: two-thumb price slider inside the filter form (was noUiSlider).
   - rowClamp: top swatch filter shows N rows with a More / Less toggle on desktop.
   - quickShop: quick view modal that loads the product's main section.
   Plus delegated clicks: quick add (.variant-button) and the review badge scroll. */
(function () {
  'use strict';

  function motion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /* Height of a sticky / fixed site header, so a scroll target is not hidden under it. */
  function headerOffset() {
    var header = document.querySelector('[data-site-header], .site-header, #shopify-section-header, header');
    if (!header) return 0;
    var pos = getComputedStyle(header).position;
    return pos === 'sticky' || pos === 'fixed' ? header.offsetHeight : 0;
  }

  document.addEventListener('alpine:init', function () {
    Alpine.data('collectionGrid', function (opts) {
      opts = opts || {};
      return {
        drawerOpen: false,
        loading: false,
        busy: false,
        init: function () {
          var self = this;
          this._change = function (e) { self.onChange(e); };
          this._click = function (e) { self.onClick(e); };
          this._pop = function () { self.render(new URL(window.location.href).searchParams, false); };
          this.$root.addEventListener('change', this._change);
          this.$root.addEventListener('click', this._click);
          window.addEventListener('popstate', this._pop);
          /* Drawer open: lock page scroll behind it */
          this.$watch('drawerOpen', function (open) {
            document.documentElement.classList.toggle('overflow-hidden', open);
          });
        },
        destroy: function () {
          this.$root.removeEventListener('change', this._change);
          this.$root.removeEventListener('click', this._click);
          window.removeEventListener('popstate', this._pop);
          document.documentElement.classList.remove('overflow-hidden');
        },

        onChange: function (e) {
          var el = e.target;
          if (el.matches('[data-sort-select]')) {
            /* Impulse reloads the page for sort */
            var q = new URLSearchParams(window.location.search);
            q.set('sort_by', el.value || el.getAttribute('data-default-sortby'));
            q.delete('page');
            window.location.search = q.toString();
            return;
          }
          var form = el.closest('form[data-filter-form]');
          if (!form || !el.name && el.type !== 'range') return;
          this.drawerOpen = false;
          /* The changed form owns its inputs; every other param (sort, q, other filters) stays */
          var params = new URLSearchParams(window.location.search);
          params.delete('page');
          Array.prototype.forEach.call(form.elements, function (input) {
            if (input.name) params.delete(input.name);
          });
          new FormData(form).forEach(function (value, key) {
            params.append(key, value);
          });
          this.render(params, true);
        },

        onClick: function (e) {
          var link = e.target.closest('a[data-filter-remove]');
          if (!link) return;
          e.preventDefault();
          this.drawerOpen = false;
          this.render(new URL(link.href, window.location.origin).searchParams, true);
        },

        scrollToGrid: function (animate) {
          var el = this.$root.querySelector('[data-scroll-to]');
          if (!el) return;
          var top = el.getBoundingClientRect().top + window.scrollY - headerOffset() - 15;
          if (Math.abs(window.scrollY - top) < 2) return;
          window.scrollTo({ top: Math.max(0, top), behavior: animate ? motion() : 'auto' });
        },

        render: function (params, push) {
          if (this.busy) return;
          var self = this;
          this.busy = true;
          this.loading = true;
          params.delete('section_id');
          var qs = params.toString();
          var pageUrl = window.location.pathname + (qs ? '?' + qs : '');
          var fetchUrl = window.location.pathname + '?' + (qs ? qs + '&' : '') + 'section_id=' + encodeURIComponent(opts.sectionId);
          this.scrollToGrid(true);

          fetch(fetchUrl, { credentials: 'same-origin' })
            .then(function (r) {
              if (!r.ok) throw new Error('Section render failed');
              return r.text();
            })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var fresh = doc.getElementById('CollectionAjaxContent');
              var current = self.$root.querySelector('#CollectionAjaxContent');
              /* e.g. a search filtered down to no results renders without the grid: load the page */
              if (!fresh || !current) { window.location.href = pageUrl; return; }
              /* Filter groups that are open now stay open in the new markup */
              current.querySelectorAll('[data-collapsible-id][data-open="true"]').forEach(function (group) {
                var id = group.getAttribute('data-collapsible-id');
                fresh.querySelectorAll('[data-collapsible-id]').forEach(function (n) {
                  if (n.getAttribute('data-collapsible-id') === id) n.setAttribute('data-force-open', '');
                });
              });
              current.innerHTML = fresh.innerHTML;
              if (push) history.pushState({ collectionGrid: true }, '', pageUrl);
              self.scrollToGrid(false);
              document.dispatchEvent(new CustomEvent('collection:reloaded'));
            })
            .catch(function () {
              window.location.href = pageUrl;
            })
            .finally(function () {
              self.busy = false;
              self.loading = false;
            });
        }
      };
    });

    /* opts: { min, max, minValue, maxValue } in currency units. The range inputs have no name;
       the hidden inputs carry the filter params and are disabled at the full range, so an untouched
       slider never adds a price filter. The change event (thumb released) applies the filter. */
    Alpine.data('priceRange', function (opts) {
      var min = parseFloat(opts.min) || 0;
      var max = parseFloat(opts.max) || 0;
      return {
        min: min,
        max: max,
        lo: Math.round(parseFloat(opts.minValue) || min),
        hi: Math.round(parseFloat(opts.maxValue) || max),
        clampLo: function () { if (this.lo > this.hi) this.lo = this.hi; },
        clampHi: function () { if (this.hi < this.lo) this.hi = this.lo; },
        trackStyle: function () {
          var span = (this.max - this.min) || 1;
          var left = (this.lo - this.min) / span * 100;
          var right = 100 - (this.hi - this.min) / span * 100;
          return 'left:' + left + '%;right:' + right + '%';
        }
      };
    });

    /* opts: { rows }. Desktop only: clamps the box to its first N rows of [data-row-item] and
       exposes clamped / expanded / limit / full for the More / Less toggle. */
    Alpine.data('rowClamp', function (opts) {
      opts = opts || {};
      var rowsShown = opts.rows || 2;
      var ro = null;
      return {
        clamped: false,
        expanded: false,
        limit: 0,
        full: 0,
        init: function () {
          var self = this;
          var raf = 0;
          this._measure = function () {
            raf = 0;
            var box = self.$refs.box;
            if (!box || window.innerWidth < 769) { self.clamped = false; return; }
            var boxTop = box.getBoundingClientRect().top;
            var rows = [];
            box.querySelectorAll('[data-row-item]').forEach(function (item) {
              var y = item.getBoundingClientRect().top - boxTop;
              if (!rows.some(function (r) { return Math.abs(r - y) < 4; })) rows.push(y);
            });
            rows.sort(function (a, b) { return a - b; });
            if (rows.length <= rowsShown) { self.clamped = false; return; }
            self.limit = Math.round(rows[rowsShown]);
            self.full = box.scrollHeight;
            self.clamped = true;
          };
          this._schedule = function () { if (!raf) raf = requestAnimationFrame(self._measure); };
          ro = new ResizeObserver(this._schedule);
          ro.observe(this.$refs.box);
          window.addEventListener('resize', this._schedule);
          this.$nextTick(this._measure);
        },
        destroy: function () {
          if (ro) ro.disconnect();
          window.removeEventListener('resize', this._schedule);
        }
      };
    });

    /* opts: { id, url, handle }. Fetches the product page once (first hover of the quick view
       button, or on open) and moves its main section into the modal; Alpine initialises the
       injected markup (productSection from product.js). Falls back to the product page. */
    Alpine.data('quickShop', function (opts) {
      var pending = null;
      return {
        open: false,
        loaded: false,
        load: function () {
          if (pending) return pending;
          var self = this;
          pending = fetch(opts.url, { credentials: 'same-origin' })
            .then(function (r) {
              if (!r.ok) throw new Error('Quick shop fetch failed');
              return r.text();
            })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var main = doc.querySelector('[id^="shopify-section-"][id$="__main"]');
              var holder = document.getElementById('QuickShopHolder-' + opts.handle);
              if (!main || !holder) throw new Error('Quick shop markup missing');
              holder.innerHTML = main.innerHTML;
              self.loaded = true;
            })
            .catch(function (err) {
              pending = null;
              throw err;
            });
          return pending;
        },
        show: function () {
          this.open = true;
          document.documentElement.classList.add('overflow-hidden');
          this.load().catch(function () { window.location.href = opts.url; });
        },
        close: function () {
          if (!this.open) return;
          this.open = false;
          document.documentElement.classList.remove('overflow-hidden');
        }
      };
    });
  });

  /* Quick add (product-card quick_add): adds one variant through theme.js's product form
     handler (data-product-form), which opens the cart drawer and refreshes it. */
  document.addEventListener('click', function (e) {
    var button = e.target.closest && e.target.closest('.variant-button[data-variant-id]');
    if (!button) return;
    e.preventDefault();
    if (button.disabled) return;
    var form = document.createElement('form');
    form.setAttribute('data-product-form', '');
    form.hidden = true;
    var id = document.createElement('input');
    id.name = 'id';
    id.value = button.getAttribute('data-variant-id');
    var qty = document.createElement('input');
    qty.name = 'quantity';
    qty.value = parseInt(button.getAttribute('data-quantity'), 10) || 1;
    form.appendChild(id);
    form.appendChild(qty);
    document.body.appendChild(form);
    button.disabled = true;
    var done = function () {
      button.disabled = false;
      window.removeEventListener('cart-ready', done);
      if (form.parentNode) form.parentNode.removeChild(form);
    };
    window.addEventListener('cart-ready', done);
    setTimeout(done, 8000);
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  });

  /* Review badge (snippets/product-review-badge.liquid): scroll to the Judge.me widget. The target is
     re-measured every frame because lazy images above it grow the page during the scroll. */
  document.addEventListener('click', function (e) {
    if (!(e.target.closest && e.target.closest('[data-review-badge]'))) return;
    var widget = document.querySelector('.jdgm-all-reviews-widget-v2025, .jdgm-review-widget, .jdgm-all-reviews-widget');
    if (!widget) return;
    e.preventDefault();
    if (motion() === 'auto') {
      window.scrollTo(0, widget.getBoundingClientRect().top + window.scrollY);
      return;
    }
    var start = window.scrollY;
    var duration = 600;
    var startTime = performance.now();
    function step(now) {
      var progress = Math.min((now - startTime) / duration, 1);
      var ease = progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      var target = widget.getBoundingClientRect().top + window.scrollY;
      window.scrollTo(0, start + (target - start) * ease);
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  });
})();
